import { ChangeYAxisSettingsProps } from '@chart/types/axis.types';
import { PaneData } from '@chart/types/create.types';
import { RIGHT_CLICK_EVENT, RightClickEvent } from '@chart/types/events.types';
import {
  DropdownList,
  DropdownListItem,
  Tooltip,
  TooltipTrigger,
} from '@design';
import React, { RefObject, useEffect, useState } from 'react';

import tooltipStyles from './OptionsMenu.module.scss';
import { OptionItem, OptionsMenuOptions } from './OptionsMenu.types';

export interface OptionsMenuLocales {
  resetChart: string;
  displayYAxis_merged: string;
  displayYAxis_split: string;
}

export interface OptionsMenuProps<T> {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  pane: T;
  changeYAxisSettings: (settings: ChangeYAxisSettingsProps) => void;
  resetChartState?: () => void;
  chartChanged: boolean;
  locales: OptionsMenuLocales;
}

const isRightClickEvent = (e: Event): e is CustomEvent<RightClickEvent> =>
  e instanceof CustomEvent;

export const OptionsMenu = <T extends PaneData>({
  canvasRef,
  changeYAxisSettings,
  pane,
  resetChartState,
  chartChanged,
  locales,
}: OptionsMenuProps<T>) => {
  const [tooltipState, setTooltipState] = useState<RightClickEvent>();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const canvasElement = canvasRef.current;

    if (!canvasElement) {
      return undefined;
    }

    const onRightClick = (event: Event) => {
      if (isRightClickEvent(event)) {
        setTooltipState(event.detail);
        setIsOpen(true);
      }
    };

    canvasElement.addEventListener(RIGHT_CLICK_EVENT, onRightClick);

    return () => {
      canvasElement.removeEventListener(RIGHT_CLICK_EVENT, onRightClick);
    };
  }, []);

  if (!tooltipState) {
    return null;
  }

  const onItemSelect = (item: OptionItem) => {
    setIsOpen(false);

    if (item.key === OptionsMenuOptions.ChangeYAxisType) {
      changeYAxisSettings({
        type: item.value,
        paneId: pane.id,
      });
    }

    if (item.key === OptionsMenuOptions.ResetChart) {
      resetChartState?.();
    }
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const hasMultipleYAxis = pane.chartAssetData.length > 1;
  const isYAxisOptionItemShown = tooltipState.isWithinYAxis && hasMultipleYAxis;
  const isResetChartOptionItemShown = !!resetChartState;

  const dropdownItems: OptionItem[] = [];

  if (isYAxisOptionItemShown) {
    dropdownItems.push({
      label:
        pane.yAxisType === 'merged'
          ? locales.displayYAxis_merged
          : locales.displayYAxis_split,
      key: OptionsMenuOptions.ChangeYAxisType,
      value: pane.yAxisType === 'merged' ? 'split' : 'merged',
    });
  }

  if (isResetChartOptionItemShown && chartChanged) {
    dropdownItems.push({
      label: locales.resetChart,
      key: OptionsMenuOptions.ResetChart,
    });
  }

  return (
    <Tooltip
      className={tooltipStyles.tooltip}
      trigger={[TooltipTrigger.Click]}
      isOpen={isOpen}
      placement="top-start"
      closeLabel=""
      hasArrow={false}
      isTouchDevice={false}
      label={
        <DropdownList
          dropdownItems={dropdownItems}
          focusedItemIndex={-1}
          shouldDisableItemMouseEvents={false}
          onItemSelect={onItemSelect}
          currentAction="hover"
          hasV2Styles
          renderDropdownItem={(item, isActive, isFocused) => (
            <DropdownListItem
              data-testid={item.key}
              label={item.label}
              isActive={isActive}
              isFocused={isFocused}
            />
          )}
        />
      }
      onClose={onClose}
    >
      <div
        style={{
          position: 'fixed',
          transform: `translate(${tooltipState.mouseX}px,${tooltipState.mouseY}px)`,
        }}
      />
    </Tooltip>
  );
};
